/* import __COLOCATED_TEMPLATE__ from './pane.hbs'; */
/* RESPONSIBLE TEAM: team-actions */

import Component from '@glimmer/component';
import { useResource } from 'ember-resources';
import { trackedFunction } from 'ember-resources/util/function';
import NavigableSelection from 'embercom/components/inbox2/common/navigable-selection-resource';
import { type ChildAction } from 'embercom/objects/inbox/command-k/action';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type CommandKService from 'embercom/services/command-k';
import type Session from 'embercom/services/session';
import { type CommandKPaneComponentArgs } from 'embercom/lib/inbox2/command-k-types';
import type TriggerProcedureAction from 'embercom/objects/inbox/command-k/trigger-procedure';
import { type TriggerableProcedure } from 'embercom/objects/inbox/command-k/trigger-procedure';
import { request } from 'embercom/lib/inbox/requests';
import { captureException } from 'embercom/lib/sentry';

interface Signature {
  Args: CommandKPaneComponentArgs<TriggerProcedureAction>;
}

export default class TriggerProcedurePaneComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare commandK: CommandKService;

  @tracked query = '';

  selection = useResource(this, NavigableSelection, () => ({
    items: this.items,
    isCommandKVisible: this.commandK.isVisible,
  }));

  private proceduresLoader = trackedFunction(this, async () => {
    try {
      let response = await request(
        `/ember/inbox/conversations/triggerable_procedures?app_id=${this.session.workspace.id}`,
      );
      let data = (await response.json()) as { procedures: TriggerableProcedure[] };
      return data.procedures;
    } catch (error: unknown) {
      captureException(error);
      return [] as TriggerableProcedure[];
    }
  });

  get isLoading(): boolean {
    return this.proceduresLoader.isLoading;
  }

  get procedures(): TriggerableProcedure[] {
    return this.proceduresLoader.value ?? [];
  }

  // Distinguish "loaded but nothing to show" from "still loading" so the
  // template can render an empty state instead of a blank list.
  get isEmpty(): boolean {
    return !this.isLoading && this.procedures.length === 0;
  }

  get items(): ChildAction[] {
    let query = this.query.toLowerCase();
    let filtered = this.procedures.filter((p) => p.name.toLowerCase().includes(query));
    return this.args.actionItem.buildResults(filtered);
  }

  @action search(query: string) {
    this.query = query;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::CommandK::TriggerProcedure::Pane': typeof TriggerProcedurePaneComponent;
    'inbox2/command-k/trigger-procedure/pane': typeof TriggerProcedurePaneComponent;
  }
}
